import { useRef, type KeyboardEvent } from 'react';
import estilos from './Pestanas.module.css';

export interface Pestana<T extends string> {
  valor: T;
  etiqueta: string;
  /** Número que acompaña a la etiqueta, si la pestaña cuenta elementos. */
  cuenta?: number;
}

interface PestanasProps<T extends string> {
  pestanas: readonly Pestana<T>[];
  activa: T;
  onCambiar: (valor: T) => void;
  /** Descripción del grupo para lectores de pantalla. */
  etiqueta: string;
}

/**
 * Pestañas para alternar entre listados de una misma pantalla.
 *
 * Además del aspecto, resuelve la navegación con las flechas del teclado, que
 * es lo que un lector de pantalla espera de un grupo de pestañas y lo que no
 * sale gratis al maquetarlas con botones sueltos.
 */
export function Pestanas<T extends string>({
  pestanas,
  activa,
  onCambiar,
  etiqueta,
}: PestanasProps<T>) {
  const contenedor = useRef<HTMLDivElement>(null);

  const alPulsarTecla = (e: KeyboardEvent<HTMLDivElement>) => {
    const direccion = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (direccion === 0) return;

    e.preventDefault();

    const indice = pestanas.findIndex((p) => p.valor === activa);
    // Se recorre en círculo: desde la última, la flecha derecha vuelve a la primera.
    const siguiente = pestanas[(indice + direccion + pestanas.length) % pestanas.length];
    if (!siguiente) return;

    onCambiar(siguiente.valor);

    const botones = contenedor.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    botones?.[pestanas.indexOf(siguiente)]?.focus();
  };

  return (
    <div
      ref={contenedor}
      className={estilos.pestanas}
      role="tablist"
      aria-label={etiqueta}
      onKeyDown={alPulsarTecla}
    >
      {pestanas.map((pestana) => {
        const esActiva = pestana.valor === activa;

        return (
          <button
            key={pestana.valor}
            type="button"
            role="tab"
            aria-selected={esActiva}
            // Sólo la pestaña activa entra en el orden de tabulación; entre
            // ellas se navega con las flechas.
            tabIndex={esActiva ? 0 : -1}
            className={[estilos.pestana, esActiva ? estilos.pestanaActiva : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onCambiar(pestana.valor)}
          >
            {pestana.etiqueta}
            {pestana.cuenta !== undefined && (
              <span className={estilos.cuenta}>{pestana.cuenta}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
